// Helpers for loading public site content from Supabase.
import { supabase } from "@/integrations/supabase/client";

type PackagePriceTable = "packages" | "visa_tax_packages";

export async function fetchPackageB2BPriceMap(packageTable: PackagePriceTable, packageIds?: string[]) {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) return new Map<string, string>();

  let query = supabase
    .from("package_b2b_prices")
    .select("package_id, price")
    .eq("package_table", packageTable);

  if (packageIds?.length) query = query.in("package_id", packageIds);

  const { data, error } = await query;
  if (error) return new Map<string, string>();
  return new Map((data ?? []).map((row: any) => [row.package_id as string, row.price as string]));
}

export async function fetchHero() {
  const { data } = await supabase.from("hero_section").select("*").eq("active", true).order("created_at", { ascending: false }).limit(1).maybeSingle();
  return data;
}
export async function fetchServices() {
  const { data } = await supabase.from("services").select("*").eq("active", true).order("display_order", { ascending: true });
  return data ?? [];
}
export async function fetchPackages() {
  const [{ data: pkgs }, { data: svcs }] = await Promise.all([
    supabase.from("packages").select("*").eq("active", true).order("display_order", { ascending: true }),
    supabase.from("services").select("id, slug, title, title_bn, service_code"),
  ]);
  const b2bPrices = await fetchPackageB2BPriceMap("packages", (pkgs ?? []).map((p: any) => p.id));
  const byCode = new Map((svcs ?? []).filter((s: any) => s.service_code).map((s: any) => [s.service_code, s]));
  const byId = new Map((svcs ?? []).map((s: any) => [s.id, s]));
  return (pkgs ?? []).map((p: any) => {
    const svc = (p.service_code && byCode.get(p.service_code)) || (p.service_id && byId.get(p.service_id)) || null;
    return { ...p, b2b_price: b2bPrices.get(p.id) ?? null, services: svc ? { slug: svc.slug, title: svc.title, title_bn: (svc as any).title_bn ?? null } : null };
  });
}
export async function fetchFaq() {
  const { data } = await supabase.from("faq").select("*").eq("active", true).order("display_order", { ascending: true });
  return data ?? [];
}
export async function fetchTestimonials() {
  const { data } = await supabase.from("testimonials").select("*").eq("active", true).order("display_order", { ascending: true });
  return data ?? [];
}
export async function fetchContactInfo() {
  const { data } = await supabase.from("contact_info").select("*").order("created_at", { ascending: false }).limit(1).maybeSingle();
  return data;
}
export async function fetchPaymentMethods() {
  const { data } = await supabase.from("payment_settings").select("*").eq("active", true).order("display_order", { ascending: true });
  return data ?? [];
}
export async function fetchSiteSettings() {
  const { data } = await supabase.from("site_settings").select("*").order("created_at", { ascending: false }).limit(1).maybeSingle();
  return data;
}
export async function fetchSeoSettings() {
  const { data } = await supabase.from("seo_settings").select("*").order("created_at", { ascending: false }).limit(1).maybeSingle();
  return data;
}
export async function fetchBlogPosts() {
  const { data } = await supabase.from("blog_posts").select("*").eq("published", true).order("published_at", { ascending: false });
  return data ?? [];
}
export async function fetchBlogPost(slug: string) {
  const { data } = await supabase.from("blog_posts").select("*").eq("slug", slug).eq("published", true).maybeSingle();
  return data;
}
export async function fetchKnowledgeHub() {
  const { data } = await supabase
    .from("knowledge_hub" as any)
    .select("*")
    .eq("status", "published")
    .order("published_at", { ascending: false });
  return (data ?? []) as any[];
}
export async function fetchKnowledgeArticle(slug: string) {
  const { data } = await supabase
    .from("knowledge_hub" as any)
    .select("*")
    .eq("slug", slug)
    .eq("status", "published")
    .maybeSingle();
  return data as any;
}
